import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiClient } from '@common/api'

/**
 * App configuration context — replaces AngularJS `appConfiguration` service.
 *
 * Loads runtime config from the backend API on mount and provides
 * it via React context. Configuration includes feature flags,
 * environment settings, and product-specific options.
 */

interface AppConfig {
    environment: string
    features: Record<string, boolean>
    branding: {
        productName: string
        logoUrl: string
    }
    session: {
        timeoutMinutes: number
        warningMinutes: number
    }
    // Fields from AngularJS appConfigurationService.js — returned by GET /api/appconfiguration
    telemetryKey?: string
    errorReportingKey?: string
    authenticationUrl?: string
    readOnlyDbConnectionOptOutWindowInMilliseconds?: number
    [key: string]: unknown
}

interface AppConfigContextType {
    config: AppConfig | null
    isLoading: boolean
    error: Error | null
    isFeatureEnabled: (featureName: string) => boolean
}

const defaultConfig: AppConfig = {
    environment: 'development',
    features: {},
    branding: {
        productName: 'Advantage Platform Solutions',
        logoUrl: '',
    },
    session: {
        timeoutMinutes: 30,
        warningMinutes: 2,
    },
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined)

interface AppConfigProviderProps {
    children: ReactNode
}

export function AppConfigProvider({ children }: AppConfigProviderProps) {
    const [config, setConfig] = useState<AppConfig | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        apiClient
            .get<AppConfig>('/appconfiguration')
            .then((data) => {
                setConfig({ ...defaultConfig, ...data })
            })
            .catch((err: unknown) => {
                console.error('Failed to load app configuration:', err)
                setError(err instanceof Error ? err : new Error(String(err)))
                // Fall back to defaults so the app remains usable
                setConfig(defaultConfig)
            })
            .finally(() => setIsLoading(false))
    }, [])

    const isFeatureEnabled = (featureName: string): boolean => {
        return config?.features[featureName] ?? false
    }

    return (
        <AppConfigContext.Provider value={{ config, isLoading, error, isFeatureEnabled }}>
            {children}
        </AppConfigContext.Provider>
    )
}

export function useAppConfig(): AppConfigContextType {
    const context = useContext(AppConfigContext)
    if (!context) {
        throw new Error('useAppConfig must be used within an AppConfigProvider')
    }
    return context
}
